import type React from "react";
import { IS_MINIZO } from "@/config/variant";

/**
 * Unizo アイコンを使ったローディング表示 (PRODUCT_DEV-583 R7-⑤)。
 *
 * variant は3種を同梱し、全画面のデフォルトは DEFAULT_UNIZO_LOADING_VARIANT の
 * 1行を書き換えるだけで切り替わる（将来の「面白いアニメーション」差し替え口）。
 * 新しい動きを足す場合は UnizoLoadingVariant に追加し、keyframes/animation を
 * tailwind.config.js（値の正本）へ追記して分岐を1つ増やす。
 *
 * 素材はベクター SVG が存在しないため PNG を使用（favicon=icon-192.png 由来の
 * 背景透過版専用アセット。生成は dev/tools/design/make-transparent-icon.js）。
 * そのためアニメは transform/opacity 系のみで構成する。
 *
 * PRODUCT_DEV-748: Minizo ビルドでは Unizo の象アイコンを出すわけにいかないが、
 * 正式ロゴがまだ無いため**テキストマーク**で代替する。動き（bounce/ripple/flip）は
 * transform/opacity だけで組んであり要素の種類に依存しないので、差し替えるのは
 * 中身の 1 要素だけで済む。正式アイコンが用意できたら Unizo と同じ `<img>` に戻す。
 */
export type UnizoLoadingVariant = "bounce" | "ripple" | "flip";

/** 全画面共通のデフォルト variant。差し替えはこの1行を変更する。 */
export const DEFAULT_UNIZO_LOADING_VARIANT: UnizoLoadingVariant = "bounce";

// R8-③: 背景透過版（icon-192.png の外周白を透過化した専用アセット）。
// bg-slate-50 等の非白背景でも白い四角が出ない。PWA アイコン群は透過化しない。
// public/ 配下の実在はテスト（UnizoLoadingIcon.test.ts）で担保。index.html で preload。
export const UNIZO_LOADING_ICON_PUBLIC_PATH = "assets/logo/unizo-icon.png";
const ICON_SRC = `${import.meta.env.BASE_URL}${UNIZO_LOADING_ICON_PUBLIC_PATH}`;

interface UnizoLoadingIconProps {
  variant?: UnizoLoadingVariant;
  /** アイコンの一辺(px)。既定 56。 */
  size?: number;
}

export const UnizoLoadingIcon: React.FC<UnizoLoadingIconProps> = ({
  variant = DEFAULT_UNIZO_LOADING_VARIANT,
  size = 56,
}) => {
  const icon = (animClass: string) =>
    IS_MINIZO ? (
      <span
        aria-hidden="true"
        style={{ width: size, height: size, fontSize: size * 0.62 }}
        className={`inline-flex items-center justify-center select-none text-primary italic font-black leading-none ${animClass}`}
      >
        M
      </span>
    ) : (
      <img
        src={ICON_SRC}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className={`select-none ${animClass}`}
        draggable={false}
      />
    );

  if (variant === "bounce") {
    // ぽよんと弾む本体 + 着地に同期してスカッシュする楕円影。
    return (
      <div
        role="status"
        aria-label="読み込み中"
        className="inline-flex flex-col items-center"
      >
        {icon("origin-bottom animate-unizoBounce")}
        <div
          className="mt-1 rounded-[50%] bg-neutral-900/20 animate-unizoShadow"
          style={{ width: size * 0.7, height: Math.max(4, size * 0.11) }}
        />
      </div>
    );
  }

  if (variant === "ripple") {
    // primary 色のリングが波紋状に拡散し、本体はゆったり呼吸スケール。
    return (
      <div
        role="status"
        aria-label="読み込み中"
        className="relative inline-grid place-items-center"
        style={{ width: size * 1.7, height: size * 1.7 }}
      >
        <span
          className="absolute rounded-full border-2 border-primary animate-unizoRipple"
          style={{ width: size, height: size }}
        />
        {icon("relative animate-unizoBreath")}
      </div>
    );
  }

  // flip: Y軸のコインフリップ（1回転 → 一拍静止のリズム）。
  return (
    <div
      role="status"
      aria-label="読み込み中"
      className="inline-block"
      style={{ perspective: 400 }}
    >
      {icon("animate-unizoFlip")}
    </div>
  );
};

export default UnizoLoadingIcon;
